import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@poseidon/ui/components/AuthContext";
import PasswordField from "@poseidon/ui/components/form/PasswordField";

function MicrosoftLogo() {
  return (
    <Box component="svg" viewBox="0 0 21 21" sx={{ width: 20, height: 20 }}>
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </Box>
  );
}

/**
 * Firebase wraps blocking-function rejections (beforeUserCreate/beforeSignIn)
 * in an auth/internal-error whose message embeds the backend's HTTP response,
 * e.g. `((HTTP request to ... returned HTTP error 403: {"error":{"message":
 * "...","status":"PERMISSION_DENIED"}})) (auth/internal-error)`. Surface the
 * backend's own message when we can find one, otherwise translate the code.
 */
function friendlyAuthError(err) {
  const embedded = /"message"\s*:\s*"([^"]+)"/.exec(err?.message ?? "");
  if (embedded) return embedded[1];
  switch (err?.code) {
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in window. Allow popups for this site and try again.";
    case "auth/network-request-failed":
      return "Couldn't reach the sign-in service. Check your connection and try again.";
    case "auth/user-disabled":
      return "This account has been disabled. Contact your administrator.";
    default:
      return "Something went wrong signing you in. Please try again, or contact your administrator if it keeps happening.";
  }
}

/**
 * Admin portal login. Sign-in is Microsoft-only: the backend mints admin
 * session claims exclusively on Microsoft sign-ins by provisioned admins, so
 * a password form here would only ever produce an access-denied session.
 *
 * The first Microsoft sign-in on an existing password account requires a
 * one-time link, driven by auth/account-exists-with-different-credential:
 * we collect the existing password, link the Microsoft credential, then ask
 * the user to press the Microsoft button again for a fresh admin session.
 */
export const MicrosoftLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signInWithMicrosoft, linkMicrosoftAccount } = useAuth();
  const [error, setError] = useState();
  const [busy, setBusy] = useState(false);
  // set when Microsoft sign-in needs a one-time link to the password account
  const [pendingLink, setPendingLink] = useState();
  const [linked, setLinked] = useState(false);

  const from = location.state?.from?.pathname || "/";

  async function handleMicrosoftSignIn() {
    setError(undefined);
    setBusy(true);
    try {
      await signInWithMicrosoft();
      navigate(from, { replace: true });
    } catch (err) {
      if (err.code === "auth/account-exists-with-different-credential") {
        setLinked(false);
        setPendingLink({ email: err.customData?.email ?? "", oauthError: err });
      } else if (err.code === "auth/popup-closed-by-user") {
        // user dismissed the popup — not an error worth surfacing
      } else {
        console.error("Microsoft sign-in failed:", err);
        setError(friendlyAuthError(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
        <LockOutlinedIcon />
      </Avatar>
      <Typography component="h1" variant="h5">
        Admin Portal
      </Typography>
      <Typography variant="body2" color="text.secondary" mt={1} mb={3}>
        Sign in with your Salt Microsoft account.
      </Typography>

      {linked && (
        <Alert severity="success" sx={{ mb: 2, width: "100%" }}>
          Microsoft account linked. Click the button below to sign in.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        fullWidth
        onClick={handleMicrosoftSignIn}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={20} /> : <MicrosoftLogo />}
        sx={{ mb: 2 }}
      >
        Sign in with Microsoft
      </Button>

      {pendingLink && !linked && (
        <>
          <Alert severity="info" sx={{ my: 2, width: "100%" }}>
            First Microsoft sign-in on this account — enter your existing Salt
            password once to link it to {pendingLink.email || "your account"}.
          </Alert>
          <Formik
            initialValues={{ password: "" }}
            onSubmit={async (values, { setSubmitting }) => {
              setError(undefined);
              try {
                await linkMicrosoftAccount(
                  pendingLink.email,
                  values.password,
                  pendingLink.oauthError
                );
                setPendingLink(undefined);
                setLinked(true);
              } catch (err) {
                console.error("Microsoft account linking failed:", err);
                setError(
                  err.code === "auth/wrong-password"
                    ? "Incorrect password."
                    : friendlyAuthError(err)
                );
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting }) => (
              <Form style={{ width: "100%" }}>
                <Field
                  autoComplete="current-password"
                  autoFocus
                  component={PasswordField}
                  fullWidth
                  id="password"
                  label="Current password"
                  margin="normal"
                  name="password"
                  required
                />
                <Button
                  variant="outlined"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={isSubmitting}
                  sx={{ mt: 1 }}
                >
                  Link Microsoft account
                </Button>
              </Form>
            )}
          </Formik>
        </>
      )}
    </>
  );
};
